/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */
import Library from './library';

enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

class Logger {
  private prefix: string = `[Nodals AI: ${Library.name} (v${Library.version})]`;
  private logLevel: LogLevel = LogLevel.ERROR;
  private static instance: Logger;

  constructor(prefix: string | null = null) {
    if (prefix) {
      this.prefix = prefix;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const logLevelParam = urlParams.get('nodals_loglevel') as string;
    if (logLevelParam) {
      const level = LogLevel[logLevelParam.toUpperCase() as keyof typeof LogLevel];
      if (level !== undefined) {
        this.logLevel = level;
      }
    }
    const pbsDebug = urlParams.get('nodals_debug') as string;
    if (['1', 'true', 'yes'].includes(pbsDebug)) {
      this.logLevel = LogLevel.DEBUG;
    }
  }

  public static getInstance(prefix: string | null = null): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(prefix);
    }
    return Logger.instance;
  }

  setLogLevel(level: LogLevel): void {
    if (!Object.values(LogLevel).includes(level)) {
      console.warn(`${this.prefix} Invalid log level: ${level}.`);
      return;
    }
    console.log(`${this.prefix} Setting log level to: ${LogLevel[level]}`);
    this.logLevel = level;
  }

  debug(message: string): void {
    if (this.logLevel <= LogLevel.DEBUG) {
      console.debug(`${this.prefix} ${message}`);
    }
  }

  info(message: string): void {
    if (this.logLevel <= LogLevel.INFO) {
      console.info(`${this.prefix} ${message}`);
    }
  }

  warn(message: string): void {
    if (this.logLevel <= LogLevel.WARN) {
      console.warn(`${this.prefix} ${message}`);
    }
  }

  error(message: string): void {
    if (this.logLevel <= LogLevel.ERROR) {
      console.error(`${this.prefix} ${message}`);
    }
  }
}

export { Logger, LogLevel };
